import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import * as lessonsService from "../services/lessons";
import * as paymentsService from "../services/payments";
import "./Payment.css";

function formatTnd(amount) {
  return new Intl.NumberFormat("fr-TN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

function transactionLimitText(limits) {
  const min = limits?.minTnd;
  const max = limits?.maxTnd;
  if (min && max) return `${min} TND - ${max} TND per transaction`;
  if (min) return `Minimum ${min} TND per transaction`;
  if (max) return `Maximum ${max} TND per transaction`;
  return "Transaction limits apply as set out in the merchant bank agreement.";
}

export default function Checkout() {
  const { packId } = useParams();
  const { accessToken } = useAuth();
  const [pack, setPack] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadCheckout() {
      setLoading(true);
      setError("");
      try {
        const [catalog, paymentConfig] = await Promise.all([
          lessonsService.fetchCatalog(),
          paymentsService.fetchClicToPayConfig(accessToken),
        ]);
        const packs = (catalog.subjects || []).flatMap((subject) => subject.packs || []);
        const selectedPack = packs.find((item) => String(item.id) === String(packId));
        if (!selectedPack) throw new Error("This lesson pack is no longer available.");
        if (!cancelled) {
          setPack(selectedPack);
          setConfig(paymentConfig);
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCheckout();
    return () => {
      cancelled = true;
    };
  }, [accessToken, packId]);

  async function handlePay() {
    if (!pack || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await paymentsService.startClicToPayCheckout(accessToken, pack.id);
      if (!response.formUrl) throw new Error("The payment page is unavailable.");
      window.location.href = response.formUrl;
    } catch (paymentError) {
      setError(paymentError.message);
      setSubmitting(false);
    }
  }

  return (
    <main className="payment-page">
      <section className="payment-panel" aria-labelledby="checkout-title">
        <Link className="payment-back" to="/courses">Back to lesson packs</Link>
        <span className="payment-kicker">Secure online payment</span>
        <h1 id="checkout-title">Confirm your lesson pack</h1>

        {loading ? <p className="payment-note">Loading your payment summary...</p> : null}
        {error ? <p className="payment-alert" role="alert">{error}</p> : null}

        {pack ? (
          <>
            <div className="payment-summary">
              <span>Lesson pack</span>
              <strong>{pack.name}</strong>
              <span>Amount due</span>
              <strong className="payment-price">{formatTnd(pack.price?.amount ?? pack.price_amount)} TND</strong>
            </div>

            <div className="payment-compliance" aria-label="Accepted cards and merchant details">
              <div>
                <span className="payment-label">Accepted cards</span>
                <div className="payment-card-marks" aria-label="Visa, MasterCard and CIB accepted">
                  <span className="payment-card-mark payment-card-mark--visa">VISA</span>
                  <span className="payment-card-mark payment-card-mark--mastercard">MasterCard</span>
                  <span className="payment-card-mark payment-card-mark--cib">CIB</span>
                </div>
              </div>
              <dl className="payment-details">
                <div><dt>Merchant affiliation no.</dt><dd>{config?.merchantAffiliationNumber || "Not configured"}</dd></div>
                <div><dt>Transaction limits</dt><dd>{transactionLimitText(config?.transactionLimits)}</dd></div>
              </dl>
            </div>

            <button type="button" className="btn primary payment-pay" onClick={handlePay} disabled={submitting || !config}>
              {submitting ? "Redirecting to ClicToPay..." : `Pay ${formatTnd(pack.price?.amount ?? pack.price_amount)} TND`}
            </button>
            <p className="payment-note">You will be redirected to ClicToPay's secure payment page. CasaClass never collects card or CVV details.</p>
          </>
        ) : null}
      </section>
    </main>
  );
}